import { Loader2, RefreshCw, Send } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import {
  useApplyPresentation,
  useRegeneratePresentation,
  type PresentationProposal,
} from '@/api/endpoints/presentation'
import { DialogContent, DialogRoot } from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'
import { errorMessage } from '@/lib/errors'

import styles from './PresentationEditDialog.module.scss'

export interface PresentationEditDialogProps {
  /** Controlled-open via parent — null/undefined = closed. The row's
   * "Sugerir mudança" button writes a target into state and clears it
   * when the dialog closes. */
  target: { videoId: string; language: string; currentTitle: string } | null
  onClose: () => void
}

/**
 * Side-by-side preview dialog: current vs proposed title + thumbnail. The
 * operator either accepts (which fires apply → YouTube write + history row)
 * or asks for a different proposal (which re-runs regenerate, telling the
 * LLM to dodge whatever's already been shown). The dialog keeps the
 * rejection list local so multiple "outras opções" never repeat.
 */
export function PresentationEditDialog({ target, onClose }: PresentationEditDialogProps) {
  const open = target !== null
  return (
    <DialogRoot open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      {target ? <DialogBody key={`${target.videoId}-${target.language}`} target={target} onClose={onClose} /> : null}
    </DialogRoot>
  )
}

interface DialogBodyProps {
  target: { videoId: string; language: string; currentTitle: string }
  onClose: () => void
}

function DialogBody({ target, onClose }: DialogBodyProps) {
  const [proposal, setProposal] = useState<PresentationProposal | null>(null)
  // Per-session ledger of titles + thumbnail styles the operator has
  // already vetoed. Sent on every regenerate so the LLM and the
  // thumbnail rotator both know what to avoid.
  const [rejectedTitles, setRejectedTitles] = useState<string[]>([])
  const [rejectedStyles, setRejectedStyles] = useState<string[]>([])

  const regenerate = useRegeneratePresentation()
  const apply = useApplyPresentation()

  // Auto-fire the first proposal as soon as the dialog opens.
  //
  // StrictMode in dev runs the effect → cleanup → effect again, which
  // (with a useRef sentinel) doubled the regen call: the first request
  // would eventually return and populate the form, but the second was
  // still pending — so ``isPending`` stayed true and the overlay/buttons
  // never recovered. The setTimeout-with-cleanup pattern dedupes
  // automatically: the first scheduled fire is cleared by the StrictMode
  // unmount before it gets a chance to run.
  useEffect(() => {
    // Auto-fire goes through the cache branch (force=false): if the
    // operator already generated a draft for this video, the backend
    // returns it instantly without burning LLM tokens or re-rendering
    // the thumbnail. They have to click "Gerar outra opção" to get a
    // fresh proposal.
    const id = window.setTimeout(() => { void runRegenerate([], [], false) }, 0)
    return () => { window.clearTimeout(id) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function runRegenerate(rt: string[], rs: string[], force: boolean): Promise<void> {
    return regenerate
      .mutateAsync({
        videoId: target.videoId,
        language: target.language,
        rejectedTitles: rt,
        rejectedThumbStyles: rs,
        force,
      })
      .then((next) => {
        setProposal(next)
      })
      .catch((err: unknown) => {
        toast.error(errorMessage(err, 'Não foi possível gerar uma proposta'))
      })
  }

  function handleRequestAnother() {
    if (!proposal) return
    const nextRejTitles = [...rejectedTitles, proposal.proposed_title]
    const nextRejStyles = [...rejectedStyles, proposal.proposed_thumbnail_style]
    setRejectedTitles(nextRejTitles)
    setRejectedStyles(nextRejStyles)
    // Explicit regen — force=true overwrites the saved draft.
    void runRegenerate(nextRejTitles, nextRejStyles, true)
  }

  function handleApply() {
    if (!proposal) return
    apply.mutate(
      {
        videoId: target.videoId,
        language: target.language,
        previewId: proposal.preview_id,
        newTitle: proposal.proposed_title,
        newDescription: proposal.proposed_description,
        newHashtags: proposal.proposed_hashtags,
        newThumbnailText: proposal.proposed_thumbnail_text,
        newHighlightWord: proposal.proposed_highlight_word,
        newThumbnailStyle: proposal.proposed_thumbnail_style,
        reason: proposal.reason,
      },
      {
        onSuccess: (data) => {
          if (data.studio_thumbnail === 'queued') {
            // A capa vertical do Short vai pelo YouTube Studio, em segundo
            // plano (card #247): o título já está no ar, a capa leva ~1 min.
            toast.success(`Título atualizado: ${data.new_title}`, {
              description: 'A capa está sendo aplicada pelo YouTube Studio — acompanhe em Jobs.',
            })
          } else {
            toast.success(`Atualizado no YouTube — novo título: ${data.new_title}`)
          }
          onClose()
        },
        onError: (err) => {
          toast.error(errorMessage(err, 'Falha ao aplicar no YouTube'))
        },
      },
    )
  }

  const isWorking = regenerate.isPending && proposal === null
  const elapsedS = useElapsedSeconds(isWorking)

  return (
    <DialogContent
      title="Sugerir mudança de título e thumbnail"
      description={`Vídeo: ${target.currentTitle || target.videoId}`}
      footer={
        <div className={styles.footer}>
          <Button variant="secondary" onClick={onClose} disabled={apply.isPending}>
            Cancelar
          </Button>
          <Button
            variant="secondary"
            leadingIcon={<RefreshCw className={regenerate.isPending && proposal !== null ? styles.spin : undefined} />}
            onClick={handleRequestAnother}
            disabled={!proposal || regenerate.isPending || apply.isPending}
          >
            Gerar outra opção
          </Button>
          <Button
            variant="primary"
            leadingIcon={apply.isPending ? <Loader2 className={styles.spin} /> : <Send />}
            onClick={handleApply}
            disabled={!proposal || apply.isPending || regenerate.isPending}
          >
            {apply.isPending ? 'Aplicando...' : 'Aplicar no YouTube'}
          </Button>
        </div>
      }
    >
      {isWorking ? (
        <div className={styles.loadingState}>
          <Loader2 className={styles.spin} />
          <span>Gerando proposta diferente... {elapsedS > 0 ? `${String(elapsedS)}s` : ''}</span>
          {elapsedS >= 5 ? (
            <small className={styles.loadingHint}>
              {elapsedS < 25
                ? 'O LLM está reescrevendo título + descrição. Em paralelo, o renderizador prepara a thumbnail nova.'
                : elapsedS < 60
                  ? 'Demora maior na primeira chamada após o servidor subir (Chromium aquecendo).'
                  : 'Algo travou? Tente cancelar e clicar Sugerir de novo.'}
            </small>
          ) : null}
        </div>
      ) : proposal === null ? (
        <div className={styles.errorState}>Nenhuma proposta disponível.</div>
      ) : (
        <div className={styles.preview}>
          <div className={styles.reasonRow}>
            <span className={styles.reasonLabel}>Por que mudar:</span>
            <span className={styles.reasonText}>{proposal.reason}</span>
          </div>

          <div className={styles.compareGrid}>
            <Side
              label="Atual"
              tone="muted"
              title={proposal.current_title}
              thumbnailUrl={proposal.current_thumbnail_path}
              caption={summarizeMetrics(proposal.metrics)}
            />
            <Side
              label="Proposta"
              tone="brand"
              title={proposal.proposed_title}
              thumbnailUrl={proposal.proposed_thumbnail_path}
              caption={`${proposal.proposed_thumbnail_text} · estilo ${proposal.proposed_thumbnail_style}`}
              loading={regenerate.isPending}
            />
          </div>

          <details className={styles.descBlock}>
            <summary>Ver descrição + hashtags propostos</summary>
            <pre className={styles.descPre}>{proposal.proposed_description}</pre>
            <div className={styles.tagRow}>
              {proposal.proposed_hashtags.map((tag) => (
                <span key={tag} className={styles.tag}>{tag}</span>
              ))}
            </div>
          </details>

          {rejectedTitles.length > 0 ? (
            <p className={styles.rejectedHint}>
              {rejectedTitles.length} proposta{rejectedTitles.length === 1 ? '' : 's'} já recusada{rejectedTitles.length === 1 ? '' : 's'} nesta sessão — a próxima evita esses ângulos.
            </p>
          ) : null}
        </div>
      )}
    </DialogContent>
  )
}

interface SideProps {
  label: string
  tone: 'muted' | 'brand'
  title: string
  thumbnailUrl: string | null | undefined
  caption: string
  loading?: boolean
}

function Side({ label, tone, title, thumbnailUrl, caption, loading }: SideProps) {
  return (
    <div className={cn(styles.side, tone === 'brand' && styles.sideBrand)}>
      <span className={styles.sideLabel}>{label}</span>
      <div className={cn(styles.thumbBox, loading && styles.thumbBoxLoading)}>
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt={`${label} thumbnail`} />
        ) : (
          <span className={styles.thumbPlaceholder}>(sem thumbnail)</span>
        )}
        {loading ? (
          <div className={styles.thumbOverlay} aria-hidden>
            <Loader2 className={styles.spin} />
          </div>
        ) : null}
      </div>
      <div className={styles.sideTitle}>{title || '(sem título)'}</div>
      <div className={styles.sideCaption}>{caption}</div>
    </div>
  )
}

function summarizeMetrics(metrics: Record<string, number | undefined> | null | undefined): string {
  if (!metrics) return ''
  const avp = typeof metrics['avp'] === 'number' ? metrics['avp'] : null
  const views = typeof metrics['views'] === 'number' ? metrics['views'] : null
  const age = typeof metrics['age_days'] === 'number' ? metrics['age_days'] : null
  const parts: string[] = []
  if (views !== null) parts.push(`${formatCount(views)} views`)
  if (avp !== null && avp > 0) parts.push(`retenção ${(avp * 100).toFixed(0)}%`)
  if (age !== null) parts.push(`${String(age)}d publicado`)
  return parts.join(' · ')
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`
  return String(n)
}

/**
 * Tracks how long the dialog has been in its loading state. Returns 0 when
 * not active, otherwise seconds since the active flag flipped to true.
 * Lets the loading copy escalate from "wait" → "explain why" → "maybe
 * something broke" without the operator needing to know how long is too
 * long.
 */
function useElapsedSeconds(active: boolean): number {
  const [s, setS] = useState(0)
  useEffect(() => {
    if (!active) {
      setS(0)
      return
    }
    setS(0)
    const start = performance.now()
    const id = window.setInterval(() => {
      setS(Math.floor((performance.now() - start) / 1000))
    }, 250)
    return () => { window.clearInterval(id) }
  }, [active])
  return s
}
